import { useState } from "react";
import { Phone, Mail, MapPin } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import QRCard from "../QRCard";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Thanks — Ben will get back to you shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Could not send message. Call us at (321) 682-6085.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-[#F4F1EA]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="overline mb-4">Get in Touch</div>
          <h2 className="h-section text-[#0B1810] mb-6">
            Free <span className="text-[#FF5A00]">Quote.</span>
            <br />
            One Call.
          </h2>
          <p className="text-lg text-[#4A4D4A] font-body mb-8 leading-relaxed">
            Got a tree that worries you? A yard that needs a reset? Call, text,
            or drop a message — Ben responds fast.
          </p>

          <div className="space-y-5">
            <a
              data-testid="contact-phone"
              href="tel:+13216826085"
              className="flex items-start gap-4 group"
            >
              <div className="w-12 h-12 bg-[#0B1810] group-hover:bg-[#FF5A00] transition-colors flex items-center justify-center flex-shrink-0">
                <Phone size={20} className="text-white" />
              </div>
              <div>
                <div className="font-accent text-xs tracking-[0.2em] uppercase text-[#4A4D4A]">
                  Call or Text
                </div>
                <div className="font-heading text-2xl text-[#0B1810] group-hover:text-[#FF5A00] transition-colors">
                  (321) 682-6085
                </div>
              </div>
            </a>

            <a
              data-testid="contact-phone-secondary"
              href="tel:+13219994458"
              className="flex items-start gap-4 group"
            >
              <div className="w-12 h-12 bg-[#0B1810] group-hover:bg-[#FF5A00] transition-colors flex items-center justify-center flex-shrink-0">
                <Phone size={20} className="text-white" />
              </div>
              <div>
                <div className="font-accent text-xs tracking-[0.2em] uppercase text-[#4A4D4A]">
                  Secondary Line
                </div>
                <div className="font-heading text-2xl text-[#0B1810] group-hover:text-[#FF5A00] transition-colors">
                  (321) 999-4458
                </div>
              </div>
            </a>

            <a
              data-testid="contact-email-primary"
              href="mailto:Lordsmith945@gmail.com"
              className="flex items-start gap-4 group"
            >
              <div className="w-12 h-12 bg-[#0B1810] group-hover:bg-[#FF5A00] transition-colors flex items-center justify-center flex-shrink-0">
                <Mail size={20} className="text-white" />
              </div>
              <div>
                <div className="font-accent text-xs tracking-[0.2em] uppercase text-[#4A4D4A]">
                  Primary Email
                </div>
                <div className="font-heading text-xl text-[#0B1810] group-hover:text-[#FF5A00] transition-colors break-all">
                  Lordsmith945@gmail.com
                </div>
              </div>
            </a>

            <a
              data-testid="contact-email-secondary"
              href="mailto:skylars3838@gmail.com"
              className="flex items-start gap-4 group"
            >
              <div className="w-12 h-12 bg-[#0B1810] group-hover:bg-[#FF5A00] transition-colors flex items-center justify-center flex-shrink-0">
                <Mail size={20} className="text-white" />
              </div>
              <div>
                <div className="font-accent text-xs tracking-[0.2em] uppercase text-[#4A4D4A]">
                  Secondary Email
                </div>
                <div className="font-heading text-xl text-[#0B1810] group-hover:text-[#FF5A00] transition-colors break-all">
                  skylars3838@gmail.com
                </div>
              </div>
            </a>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#0B1810] flex items-center justify-center flex-shrink-0">
                <MapPin size={20} className="text-white" />
              </div>
              <div>
                <div className="font-accent text-xs tracking-[0.2em] uppercase text-[#4A4D4A]">
                  Service Area
                </div>
                <div className="font-heading text-xl text-[#0B1810]">
                  Brevard County, Florida
                </div>
                <div className="font-body text-sm text-[#4A4D4A] mt-1">
                  Rockledge · Melbourne · Cocoa · Cocoa Beach · Merritt Island
                  · Cape Canaveral · Titusville · Viera
                </div>
              </div>
            </div>

            <div className="pt-4">
              <QRCard
                variant="dark"
                size={140}
                label="Scan to Visit"
                sub="Share with neighbors"
                testId="qr-contact"
              />
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="bg-white p-8 md:p-10 border-2 border-[#0B1810] shadow-[10px_10px_0_#0B1810]"
          >
            <h3 className="font-heading text-3xl md:text-4xl mb-6 text-[#0B1810]">
              Send a Message
            </h3>
            <div className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="hc-label" htmlFor="c-name">
                    Name
                  </label>
                  <input
                    id="c-name"
                    data-testid="contact-name-input"
                    className="hc-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="hc-label" htmlFor="c-phone">
                    Phone (optional)
                  </label>
                  <input
                    id="c-phone"
                    data-testid="contact-phone-input"
                    className="hc-input"
                    type="tel"
                    value={form.phone}
                    onChange={(e) =>
                      setForm({ ...form, phone: e.target.value })
                    }
                  />
                </div>
              </div>
              <div>
                <label className="hc-label" htmlFor="c-email">
                  Email
                </label>
                <input
                  id="c-email"
                  data-testid="contact-email-input"
                  className="hc-input"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="hc-label" htmlFor="c-msg">
                  Message
                </label>
                <textarea
                  id="c-msg"
                  data-testid="contact-message-input"
                  className="hc-textarea"
                  value={form.message}
                  onChange={(e) =>
                    setForm({ ...form, message: e.target.value })
                  }
                  placeholder="Tell us about the job..."
                  required
                />
              </div>
              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={loading}
                className="btn-primary w-full justify-center disabled:opacity-60"
              >
                {loading ? "Sending..." : "Send Message"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
